<template>
  <RouterLink
    :to="{ name: 'VaTranscription', params: { id: zoomPhoneResult.id }, query: linkQuery }"
    class="link"
  >
    <td>
      {{ getTextWithFallBack(zoomPhoneResult.callId) }}
    </td>
    <td>
      <div class="caller-user">
        <BAvatar
          :image-url="zoomPhoneResult.callerUser?.avatarUrl"
          :name="zoomPhoneResult.callerUser?.name"
          size="40px"
        />
        <div>
          {{ getTextWithFallBack(zoomPhoneResult.callerUser?.name) }}
        </div>
      </div>
    </td>
    <td>{{ getTextWithFallBack(zoomPhoneResult.callerDidNumber) }}</td>
    <td>{{ getTextWithFallBack(zoomPhoneResult.calleeDidNumber) }}</td>
    <td>{{ getDateAndTimeWithFallBack(zoomPhoneResult.startedAt) }}</td>
    <td>{{ getDateAndTimeWithFallBack(zoomPhoneResult.endedAt) }}</td>
    <td>{{ getCallTimeWithFallBack(zoomPhoneResult.startedAt, zoomPhoneResult.endedAt) }}</td>
    <td class="space-column" />
    <td class="manual-analysis-column">
      <BBtn
        v-if="zoomPhoneResult.callId && zoomPhoneResult.voiceAnalysisStatus !== VoiceAnalysisStatus.Succeeded"
        type="primary"
        size="small"
        :disabled="isAnalyzing || zoomPhoneResult.voiceAnalysisStatus === VoiceAnalysisStatus.Processing"
        class="analyze-btn"
        @click.prevent="handleAnalyze"
      >
        {{ $t('vaApp.voiceAnalysis.analyze') }}
      </BBtn>
      <div
        v-else
        class="analysis-complete-tag"
      >
        {{ $t('vaApp.voiceAnalysis.completed') }}
      </div>
    </td>
  </RouterLink>
</template>

<script setup lang="ts">
import { ZoomPhoneResult, VoiceAnalysisStatus } from '@/api/openapi';
import { altText } from '@/utils/alt';
import { formatSecondsToTime, formatShorterDateTime, getDiff } from '@/utils/date-time';

const props = defineProps<{
  zoomPhoneResult: ZoomPhoneResult;
  isAnalyzing: boolean;
}>();

const emit = defineEmits<{
  analyze: [id: number];
}>();

// useQueryParameterがvue-routerの値を更新しないため、ブラウザのURLから直接クエリパラメータを取得
const params = new URLSearchParams(window.location.search);
const linkQuery = {
  page: Number(params.get('page')) || 1,
  startedAtFrom: params.get('startedAtFrom') || undefined,
  startedAtTo: params.get('startedAtTo') || undefined,
  callerUserId: params.get('callerUserId') || undefined,
  calleeDidNumber: params.get('calleeDidNumber') || undefined,
};

const handleAnalyze = () => {
  emit('analyze', props.zoomPhoneResult.id);
};

const getTextWithFallBack = (str: string) => {
  return altText(str);
};

const getDateAndTimeWithFallBack = (date: string) => {
  return altText(formatShorterDateTime(date));
};

const getCallTimeWithFallBack = (to: string, from: string) => {
  return altText(formatSecondsToTime(getDiff(to, from, 'seconds')));
};
</script>

<style lang="scss" scoped>
.link {
  display: contents;
}

td {
  width: 160px;
  height: 80px;
  padding-inline: $basespace-200;
  color: #000;
  font-family: Rubik;
  font-size: $fontsize-200;
  font-style: normal;
  font-weight: 400;
  line-height: 12px;

  vertical-align: middle;

  &:first-child {
    padding-left: 40px;
    width: 230px;
  }

  &.space-column {
    width: auto;
  }

  &.manual-analysis-column {
    padding-right: $basespace-600;
    text-align: center;
  }
}

.caller-user {
  display: flex;
  align-items: center;
  gap: 12px;
}

.analysis-complete-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: $bgcolor-base;
  color: $textcolor-base;
  font-size: $fontsize-100;
  padding: $basespace-50 $basespace-100;
  border-radius: 14px;
  line-height: $fontsize-600;
  white-space: nowrap;
}
</style>
